import { apiPost } from '@gat-ship/http-client'
import {
  searchContextItems,
  type SearchFunctionMap,
  type SearchFunctionOptions,
  type SearchResult
} from '@gat-ship/types'
import type {
  SearchForStakeholderRequestPayload,
  SearchForStakeholderRequestResponse,
  StakeholderType,
  Stakeholder,
  SearchForStakeholderContactRequestResponse,
  StakeholderContact,
  SearchForStakeholderContactRequestPayload
} from '@gat-ship/types'
import {
  IconClient,
  IconOrganization,
  IconUser,
  IconVendor
} from '@gat-ship/icons'
import { globalUtils } from '@gat-ship/utils'
import type { ModalStack } from '@gat-ship/vue-components'

export interface GetStakeholdersRequestParams {
  /** The type of the stakeholders ('client', 'vendor' or 'other'). */
  stakeholder_type: StakeholderType
  /** Optionally include inactive (soft removed). If omitted, inactive stakeholders are excluded by default. */
  include_inactive: boolean
}

function getStakeholderContextType(stakeholderType: StakeholderType) {
  switch (stakeholderType) {
    case 'client':
      return searchContextItems.client
    case 'vendor':
      return searchContextItems.vendors
    case 'other':
      return searchContextItems.otherParties
    default:
      throw new Error('Invalid stakeholder type.')
  }
}

function getStakeholderIcon(stakeholderType: StakeholderType) {
  switch (stakeholderType) {
    case 'client':
      return IconClient
    case 'vendor':
      return IconVendor
    case 'other':
      return IconOrganization
    default:
      throw new Error('Invalid stakeholder type.')
  }
}

function getContactTitle(contact: StakeholderContact): string {
  if (contact.contactType === 'person') {
    return contact.firstName + ' ' + contact.lastName
  }
  return contact.otherName!
}

function constructStakeholderSearchQuery(
  search: string | undefined,
  stakeholderType: StakeholderType
): string {
  return `type:"${stakeholderType}" ${search ?? ''}`
}

export async function contactsSearchRequest(
  params: SearchForStakeholderContactRequestPayload,
  options?: StakeholdersSearchRequestOptions
): Promise<SearchResult<StakeholderContact>[]> {
  const commandId = globalUtils.functions.uuid()
  const response = await apiPost<
    SearchForStakeholderContactRequestResponse,
    SearchForStakeholderContactRequestPayload
  >(`stakeholders/contacts/search`, params, commandId, {
    signal: options?.signal
  })
  return response.contacts.map((contact) => ({
    id: contact.id,
    title: getContactTitle(contact),
    contextType: searchContextItems.contacts,
    subTitle: contact.email ?? undefined,
    icon: contact.contactType === 'person' ? IconUser : IconOrganization,
    metadata: contact,
    actions: { selected: () => {} }
  }))
}

/** Gets a list of stakeholders for a given type. */
export interface StakeholdersSearchRequestOptions {
  signal?: AbortSignal
}

export async function stakeholdersSearchRequest(
  params: SearchForStakeholderRequestPayload,
  options?: StakeholdersSearchRequestOptions
): Promise<SearchResult<Stakeholder>[]> {
  const commandId = globalUtils.functions.uuid()
  const response = await apiPost<
    SearchForStakeholderRequestResponse,
    SearchForStakeholderRequestPayload
  >(`stakeholders/search`, params, commandId, {
    signal: options?.signal
  })
  return response.stakeholders.map((stakeholder) => ({
    id: stakeholder.id,
    title: stakeholder.name,
    contextType: getStakeholderContextType(stakeholder.stakeholderType),
    subTitle: stakeholder.name,
    icon: getStakeholderIcon(stakeholder.stakeholderType),
    metadata: stakeholder,
    actions: { selected: () => {} }
  }))
}

export async function allStakeholdersSearchRequest(
  params: Omit<SearchForStakeholderRequestPayload, 'stakeholderType'>
): Promise<SearchResult<Stakeholder>[]> {
  const promises: Promise<SearchResult<Stakeholder>[]>[] = []
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  promises.push(
    stakeholdersSearchRequest({
      ...params
    })
  )
  const results = await Promise.all(promises)
  return results.flat()
}

function withStackOpen(
  results: SearchResult<Stakeholder>[],
  stack: ModalStack
): SearchResult<Stakeholder>[] {
  return results.map((r) => ({
    ...r,
    actions: {
      selected: () =>
        stack.open('stakeholder', {
          id: r.id,
          stakeholderType: r.metadata!.stakeholderType
        })
    }
  }))
}

function withContactsStackOpen(
  results: SearchResult<StakeholderContact>[],
  stack: ModalStack
): SearchResult<StakeholderContact>[] {
  return results.map((r) => ({
    ...r,
    actions: {
      selected: () =>
        stack.open('stakeholder', {
          id: r.metadata!.stakeholderId,
          stakeholderType: r.metadata!.stakeholderType,
          stakeholderSection: 'contacts'
        })
    }
  }))
}

export function createStakeholderSearchFunctions(
  stack: ModalStack
): SearchFunctionMap {
  return {
    client: async (search, options) =>
      withStackOpen(
        await stakeholdersSearchRequest(
          {
            query: constructStakeholderSearchQuery(search, 'client'),
            queryMode: 'free-text'
          },
          { signal: options?.signal }
        ),
        stack
      ),
    vendors: async (search, options) =>
      withStackOpen(
        await stakeholdersSearchRequest(
          {
            query: constructStakeholderSearchQuery(search, 'vendor'),
            queryMode: 'free-text'
          },
          { signal: options?.signal }
        ),
        stack
      ),
    otherParties: async (search, options) =>
      withStackOpen(
        await stakeholdersSearchRequest(
          {
            query: constructStakeholderSearchQuery(search, 'other'),
            queryMode: 'free-text'
          },
          { signal: options?.signal }
        ),
        stack
      ),
    contacts: async (search, options) =>
      withContactsStackOpen(
        await contactsSearchRequest(
          {
            query: search ?? '',
            queryMode: 'free-text',
            includeTotalCount: false,
            pageNumber: 1,
            pageSize: 10,
            totalLimit: 10
          },
          { signal: options?.signal }
        ),
        stack
      ),
    global: async (search, options) =>
      withStackOpen(
        await stakeholdersSearchRequest(
          {
            query: search ?? '',
            queryMode: 'free-text'
          },
          { signal: options?.signal }
        ),
        stack
      )
  }
}
